import { Paragraph, Spinner } from '@sbab-ui/components'
import { Config } from '../apps/config'
import { AppState } from '../state/fixedAppState'
import Callout from './Callout'
import CustomButton from './CustomButton'
import SlideOver from './SlideOver'
import SlideOverContentCorporateFixed from './SlideOverContentCorporateFixed'
import SlideOverContentPrivateFixed from './SlideOverContentPrivateFixed'
import CorporateSavingsSVG from './icons/CorporateSavingsSVG'
import SavingsSVG from './icons/SavingsSVG'

interface Props {
  appState: AppState
  buttons: Config['buttons']
  variant: Config['name']
}

function toCurrency(number: number) {
  const formatter = new Intl.NumberFormat('sv-SE', {
    style: 'decimal',
    currency: 'SEK',
  })
  return formatter.format(number)
}

export default function FixedResult({ appState, buttons, variant }: Readonly<Props>) {
  function getButtonConfig() {
    // The window.scaConfig?.buttonConfig is feature toggleable
    const customButtonConfig = window.scaConfig?.buttonConfig
    if (customButtonConfig) {
      return customButtonConfig.map((item, index) => {
        return { ...item, id: index }
      })
    } else return buttons
  }
  const buttonConfig = getButtonConfig()
  const { calculationState, interestRateObjs, interestRateIndex, userIsTyping, placementAmount } =
    appState
  const { amount, minAmount, maxAmount } = interestRateObjs[interestRateIndex]
  const totalAmount = Math.floor(+amount) + placementAmount
  const hasCalculationError = calculationState !== 'ok'
  const blur = userIsTyping || hasCalculationError
  return (
    <div className={`relative ${userIsTyping ? 'text-neutral-95' : 'text-black'}`}>
      {userIsTyping ? (
        <div className="relative">
          <div className="w-full z-20 absolute left-0 right-0 mx-auto flex justify-center">
            <Spinner />
          </div>
        </div>
      ) : null}
      {hasCalculationError ? <Callout minAmount={minAmount} maxAmount={maxAmount} /> : null}
      <div
        className={`sm:mb-10 max-w-[400px] sm:max-w-[592px] ${
          blur ? 'opacity-5 pointer-events-none outline-none' : ''
        }`}
      >
        <div className="absolute flex h-28 w-[calc(100%-32px)] max-w-[678px] flex-row-reverse sm:h-auto sm:w-[calc(100%-48px)]">
          {variant === 'corporateFixed' ? (
            <CorporateSavingsSVG blur={false} />
          ) : (
            <SavingsSVG blur={false} />
          )}
        </div>
        <div data-cs-mask className="relative z-10 flex min-h-[129px] flex-col justify-center">
          <span
            className="mb-4 text-2xl min-[375px]:text-3xl font-medium leading-[56px] sm:text-7xl"
            data-cy="result"
          >
            {toCurrency(totalAmount)} kr
          </span>
          <SlideOver
            buttonText="Så har vi räknat"
            title="Så har vi räknat"
            type="iconRight"
            disabled={blur}
          >
            {variant === 'corporateFixed' ? (
              <SlideOverContentCorporateFixed appState={appState} />
            ) : (
              <SlideOverContentPrivateFixed appState={appState} />
            )}
          </SlideOver>
        </div>
      </div>
      <div className="sm:h-8" />
      <div className="flex flex-col sm:flex-row sm:flex-wrap">
        {buttonConfig.map((button) => (
          <CustomButton
            href={button.href}
            key={button.id}
            variant={button.variant}
            text={button.text}
          />
        ))}
      </div>
      <Paragraph className="text-black">
        <strong>Bra att veta:</strong>{' '}
        {variant === 'corporateFixed' ? (
          <>För att öppna ett placeringskonto behöver ni först öppna ett SBAB-konto.</>
        ) : (
          <>För att öppna ett fasträntekonto behöver du först öppna ett sparkonto.</>
        )}
      </Paragraph>
    </div>
  )
}
